import { useEffect, useRef } from 'react';
import { type ChatMessageItem } from '../../../components/chat/ChatMessage';

export const useChatAutoScroll = ({
  messages,
  isStreaming,
}: {
  messages: ChatMessageItem[];
  isStreaming: boolean;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const scrollFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !shouldAutoScrollRef.current) {
      return;
    }

    if (scrollFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      scrollFrameRef.current = null;
    });
  }, [messages, isStreaming]);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  const handleMessageScroll = () => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 120;
  };

  return { scrollContainerRef, handleMessageScroll };
};
